import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { filter, find, get, includes, isArray, isEmpty, orderBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, DialogContent, DialogTitle } from '@mui/material';
import {
  IComment,
  IDialogChangeStateOrPriority,
  ISelectItem,
  IStudyDetailComments,
} from './_types';
import { IPriorityConfig, IStatusConfig } from 'modules/Administration/Workplaces/_types';
import { editComment, exportComment, setActiveComment } from './_api';
import { getUserStatuses } from 'modules/Administration/Users/_api';
import { getPriorityStatusConfig } from 'modules/Administration/Workplaces/_api';
import StudyDetailCommentForm from './StudyDetailCommentForm';
import StudyDetailCommentStatePriorityForm from './StudyDetailCommentStatePriorityForm';
import { useAppGlobals } from 'utils/hooks/useAppGlobals';
import { useStudy } from 'utils/hooks/useStudy';
import { useActions } from 'utils/hooks/useActions';
import { useAppInfo } from 'utils/hooks/useAppInfo';
import { useUser } from 'utils/hooks/useUser';
import { stateIsSame } from 'utils/componentOptimizatons';
import { useLanguage } from 'utils/hooks/useLanguage';
import { CheckedButton } from 'components/Buttons/CheckedButton';
import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog';
import useAlerts from 'components/Alerts/useAlerts';
import { Announcement } from 'components/Announcement/Announcement';
import StudyDetailComment from 'components/StudyDetailComment/StudyDetailComment';
import { FINISHED } from 'constants/constants';

const formatDateTime: string = 'dd. MM. yyyy, HH:mm:ss';

const PlainStudyDetailComments: React.FC<IStudyDetailComments> = ({
  study,
  setStudy,
  certNameItems,
}) => {
  const { t } = useTranslation('StudyDetail');
  const { hasRole } = useUser();
  const { currentLocale } = useLanguage();
  const [commentItem, setCommentItem] = useState<any>(null);
  const [dialogState, setDialogState] = useState<boolean>(false);
  const [dialogChangeStateOrPriority, setDialogChangeStateOrPriority] =
    useState<IDialogChangeStateOrPriority>({ dialogState: false });
  const { addErrorAlert, addSuccessAlert } = useAlerts();
  const { toggleLoader } = useAppGlobals();
  const { compactMode, confirmationData, lastPageState, priorityAndStatusEnabled } = useAppInfo();
  const { storeConfirmationData } = useActions();
  const { getStudyCommentsByStudyID, studyIsLocked, isCommentsSigned } = useStudy();
  const [comments, setComments] = useState<IComment[]>([]);
  const [userStatuses, setUserStatuses] = useState<string[]>([]);
  const [statusItems, setStatusItems] = useState<ISelectItem[]>([]);
  const [allStatusItems, setAllStatusItems] = useState<ISelectItem[]>([]);
  const [priorityItems, setPriorityItems] = useState<ISelectItem[]>([]);
  const [stateHasToBeSigned, setStateHasToBeSigned] = useState<string>('');
  const [emailToPhysicians, setEmailToPhysicians] = useState<string[]>([]);
  const [commentStatusAndPriorityCode, setCommentStatusAndPriorityCode] = useState<any>();
  const [allStatuses, setAllStatuses] = useState<IStatusConfig[]>([]);
  const [allPriorities, setAllPriorities] = useState<IPriorityConfig[]>([]);
  const [showOnlyValid, setShowOnlyValid] = useState<boolean>(false);

  const isLocked = useMemo(
    () => studyIsLocked(study),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [study],
  );

  const [initialComment, setInitialComment] = useState<any>(null);

  const getEntities = async () => {
    toggleLoader();
    try {
      const [userStatuses, prioritiesAndStatuses] = await Promise.all([
        getUserStatuses(),
        getPriorityStatusConfig(),
      ]);
      if (!isEmpty(userStatuses) && isArray(userStatuses)) {
        setUserStatuses(userStatuses);
      }

      if (!isEmpty(prioritiesAndStatuses)) {
        const statuses = get(prioritiesAndStatuses, 'statuses', []) || [];
        const priorities = get(prioritiesAndStatuses, 'priorities', []) || [];
        setAllStatuses(statuses);
        setAllPriorities(priorities);
        const statusItems: ISelectItem[] = [];
        const allStatusItems: ISelectItem[] = [];
        statuses.forEach((item) => {
          const prepaireItem = {
            id: item.code,
            label: get(item, `name.${currentLocale}`, get(item, 'name.cs', 'name')),
          };
          if (
            !isEmpty(userStatuses) &&
            isArray(userStatuses) &&
            includes(userStatuses, item.code)
          ) {
            statusItems.push(prepaireItem);
          }
          allStatusItems.push(prepaireItem);
        });
        setStatusItems(statusItems);
        setAllStatusItems(allStatusItems);

        setPriorityItems(
          priorities.map((item) => ({
            id: item.code,
            label: get(item, `name.${currentLocale}`, get(item, 'name.cs', 'name')),
            color: get(item, 'color', 'primary') || 'primary',
          })),
        );
        const emailToPhysicians: string[] = [];
        statuses.forEach((status) => {
          if (status.hasToBeSigned) {
            setStateHasToBeSigned(status.code);
          }
          if (status.emailToPhysician) {
            emailToPhysicians.push(status.code);
          }
        });
        setEmailToPhysicians(emailToPhysicians);
      }
    } catch (e) {
      console.debug(e);
    }

    toggleLoader(false);
  };

  useEffect(() => {
    if (priorityAndStatusEnabled) {
      getEntities();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priorityAndStatusEnabled]);

  const getAllComments = useCallback(async () => {
    const archiveID = get(study, 'archive.id');
    const studyID = get(study, 'studyInstanceUid');
    if (archiveID && studyID) {
      toggleLoader();
      const comments = await getStudyCommentsByStudyID(archiveID, studyID);
      setComments(comments);
      let commentStatusAndPriorityCode = {};
      let checkCommentSignatures: number[] = [];
      comments.forEach((comment) => {
        const status = { ...find(allStatuses, { code: comment.commentStatusCode || '' }) };
        commentStatusAndPriorityCode = {
          ...commentStatusAndPriorityCode,
          [comment.id]: {
            commentPriorityCode: comment.commentPriorityCode || '',
            commentStatusCode: comment.commentStatusCode || '',
            status,
          },
        };
        const isDeprecated = get(comment, 'deprecated', false);
        const isFinished = get(status, 'type') === FINISHED;
        if (isFinished && !isDeprecated) {
          checkCommentSignatures.push(comment.id);
        }
      });
      let signedComments = [];
      if (!isEmpty(checkCommentSignatures)) {
        signedComments = await isCommentsSigned(checkCommentSignatures);
      }
      signedComments.forEach((signedComment) => {
        commentStatusAndPriorityCode = {
          ...commentStatusAndPriorityCode,
          [signedComment.id]: {
            ...get(commentStatusAndPriorityCode, [signedComment.id], {}),
            signed: get(signedComment, 'signed'),
          },
        };
      });
      setCommentStatusAndPriorityCode(commentStatusAndPriorityCode);
      toggleLoader(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [study, allStatuses, stateHasToBeSigned, emailToPhysicians]);

  // Load all comments
  useEffect(() => {
    getAllComments();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [study, allStatuses, stateHasToBeSigned, emailToPhysicians]);

  const saveStatusOrPriority = async (comment: IComment, priority: string, state: string) => {
    toggleLoader();
    const prepareValues = {
      comment: {
        id: get(comment, 'id', 0) || (0 as number),
        text: get(comment, 'text', ''),
      },
      ...(priority ? { commentPriorityCode: priority } : {}),
      ...(state ? { commentStatusCode: state } : {}),
    };
    await editComment(prepareValues).then(async () => {
      await getAllComments();
    });
    toggleLoader(false);
  };

  const handleChangeStatus = (state: string, comment: IComment) => {
    const emailToPhysician = includes(emailToPhysicians, state);
    const hasToBeSigned = state === stateHasToBeSigned || state === 'manualStateHasToBeSigned';
    if (emailToPhysician || hasToBeSigned) {
      setDialogChangeStateOrPriority({
        dialogState: true,
        comment,
        state: state === 'manualStateHasToBeSigned' ? undefined : state,
        emailToPhysician,
        hasToBeSigned,
      });
    } else {
      saveStatusOrPriority(comment, '', state);
      setCommentStatusAndPriorityCode({
        ...commentStatusAndPriorityCode,
        [get(comment, 'id', 0)]: {
          ...get(commentStatusAndPriorityCode, [get(comment, 'id', 0)]),
          commentStatusCode: state,
        },
      });
    }
  };

  useMemo(() => {
    const commentForm = get(lastPageState, 'form.comment', null);
    if (commentForm != null) {
      const comment = get(commentForm, 'values.comment.text', null);
      if (comment !== null) {
        setInitialComment(comment);
        setDialogState(true);
      }
    }
  }, [lastPageState, setDialogState]);

  const onChangeActive = async (id: string) => {
    toggleLoader();
    const changed = await setActiveComment(id, false);
    if (changed) {
      await getAllComments();
    }
    toggleLoader(false);
  };
  const onExportComment = async (id: string, text: string) => {
    toggleLoader();
    const changed = await exportComment({ comment: { id, text }, study });
    if (changed) {
      addSuccessAlert(t('commentExported'));
    } else {
      addErrorAlert(t('commentNotExported'));
    }
    toggleLoader(false);
  };
  const clearAction = () => {
    storeConfirmationData(null);
  };
  const cancelAction = () => {
    clearAction();
  };
  const confirmAction = async () => {
    onChangeActive(get(confirmationData, 'id'));
    clearAction();
  };
  const confirmActionExportComment = async () => {
    onExportComment(get(confirmationData, 'id'), get(confirmationData, 'text'));
    clearAction();
  };

  const renderedComments = useMemo(() => {
    const commentsForDisplay = !isArray(comments)
      ? []
      : showOnlyValid
        ? filter(comments, ['deprecated', false])
        : comments;
    const renderedCommentForDisplay = orderBy(commentsForDisplay, ['createdWhen'], ['desc']).map(
      (comment, index) => {
        /**
         * Pokud má komentář dodatky, tak zpracuji dodatky a zobrazím pod komentářem
         */
        const supplements = get(comment, 'supplements', []);
        let renderedSupplementForDisplay;
        if (!isEmpty(supplements)) {
          const supplementsForDisplay: IComment[] = !isArray(supplements)
            ? []
            : showOnlyValid
              ? filter(supplements, ['deprecated', false])
              : supplements;
          if (!isEmpty(supplementsForDisplay)) {
            renderedSupplementForDisplay = orderBy(
              supplementsForDisplay,
              ['createdWhen'],
              ['desc'],
            ).map((comment, index) => {
              const commentStatusCode = get(comment, 'commentStatusCode', null);
              const commentStatusCodeTranslation: any = commentStatusCode
                ? get(find(allStatusItems, { id: commentStatusCode }), 'label', '')
                : '';
              const allowedNextStatuses = get(
                find(allStatuses, { code: commentStatusCode }),
                'allowedNextStatuses',
                [],
              );
              const allowedNextUserStatuses: any[] = [];
              allowedNextStatuses.forEach((item) => {
                if (includes(userStatuses, get(item, 'code'))) {
                  allowedNextUserStatuses.push(item);
                }
              });
              const RenderedStudyDetailComment = (
                <React.Fragment key={comment.id ?? index}>
                  <StudyDetailComment
                    comment={comment}
                    index={index}
                    commentStatusAndPriorityCode={commentStatusAndPriorityCode}
                    commentStatusCodeTranslation={commentStatusCodeTranslation}
                    priorityItems={priorityItems}
                    allowedNextUserStatuses={allowedNextUserStatuses}
                    formatDateTime={formatDateTime}
                    isLocked={isLocked}
                    setDialogState={setDialogState}
                    setCommentItem={setCommentItem}
                    handleChangeStatus={handleChangeStatus}
                    commentsLength={supplementsForDisplay.length}
                  />
                  {}
                </React.Fragment>
              );
              return RenderedStudyDetailComment;
            });
          }
        }

        /**
         * Zpracovat komentář a případně přidat dodatky
         */
        const commentStatusCode = get(comment, 'commentStatusCode', null);
        const commentStatusCodeTranslation: any = commentStatusCode
          ? get(find(allStatusItems, { id: commentStatusCode }), 'label', '')
          : '';
        const allowedNextStatuses = get(
          find(allStatuses, { code: commentStatusCode }),
          'allowedNextStatuses',
          [],
        );
        const allowedNextUserStatuses: any[] = [];
        allowedNextStatuses.forEach((item) => {
          if (includes(userStatuses, get(item, 'code'))) {
            allowedNextUserStatuses.push(item);
          }
        });
        const RenderedStudyDetailComment = (
          <React.Fragment key={comment.id ?? index}>
            <StudyDetailComment
              comment={comment}
              index={index}
              commentStatusAndPriorityCode={commentStatusAndPriorityCode}
              commentStatusCodeTranslation={commentStatusCodeTranslation}
              priorityItems={priorityItems}
              allowedNextUserStatuses={allowedNextUserStatuses}
              formatDateTime={formatDateTime}
              isLocked={isLocked}
              setDialogState={setDialogState}
              setCommentItem={setCommentItem}
              handleChangeStatus={handleChangeStatus}
              commentsLength={commentsForDisplay.length}
              renderedSupplementForDisplay={renderedSupplementForDisplay}
            />
          </React.Fragment>
        );
        return RenderedStudyDetailComment;
      },
    );
    return renderedCommentForDisplay;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    comments,
    confirmationData,
    dialogState,
    commentStatusAndPriorityCode,
    statusItems,
    dialogChangeStateOrPriority,
    showOnlyValid,
  ]);

  return (
    <>
      {hasRole('ROLE_COMMENTARY_ALL') || hasRole('ROLE_COMMENTARY_MINE') ? (
        <Button
          size={compactMode ? 'small' : 'medium'}
          variant="contained"
          color="primary"
          onClick={() => {
            setDialogState(true);
            setCommentItem(null);
          }}
          sx={{ mb: 2 }}
        >
          {t('action.addComment')}
        </Button>
      ) : null}
      {priorityAndStatusEnabled && (
        <CheckedButton
          active={showOnlyValid}
          onClick={() => setShowOnlyValid(!showOnlyValid)}
          leftMargin={true}
          topMargin={false}
          sx={{ mb: 2 }}
        >
          {t('PriorityStatusesConfig:showOnlyValid')}
        </CheckedButton>
      )}
      {renderedComments}
      <Dialog
        open={dialogState}
        onClose={() => setDialogState(false)}
        aria-labelledby="form-dialog-title"
        maxWidth="xl"
        fullWidth={true}
      >
        <DialogTitle id="form-dialog-title">
          {t(
            get(commentItem, 'hasCreateSupplement', false)
              ? 'dialog_comment_supplement'
              : 'dialog_comment',
          )}
        </DialogTitle>
        <DialogContent>
          <StudyDetailCommentForm
            setDialogState={setDialogState}
            commentItem={commentItem}
            getComments={getAllComments}
            study={study}
            setStudy={setStudy}
            initialComment={initialComment}
            allStatuses={allStatuses}
            allPriorities={allPriorities}
          />
        </DialogContent>
      </Dialog>
      <Dialog
        open={dialogChangeStateOrPriority.dialogState}
        onClose={() => setDialogChangeStateOrPriority({ dialogState: false })}
        aria-labelledby="form-dialog-title"
        maxWidth="sm"
        fullWidth={true}
      >
        <DialogTitle id="form-dialog-title">{t('dialog_comment')}</DialogTitle>
        <DialogContent>
          <StudyDetailCommentStatePriorityForm
            commentItem={dialogChangeStateOrPriority.comment}
            hasMailSend={dialogChangeStateOrPriority.emailToPhysician}
            hasToBeSigned={dialogChangeStateOrPriority.hasToBeSigned}
            priority={dialogChangeStateOrPriority.priority}
            state={dialogChangeStateOrPriority.state}
            setDialogState={setDialogChangeStateOrPriority}
            getComments={getAllComments}
            certNameItems={certNameItems}
          />
        </DialogContent>
      </Dialog>
      {confirmationData && confirmationData.id && (
        <ConfirmationDialog
          title={confirmationData.title ? t(confirmationData.title) : t('confirmDelete')}
          open={true}
          aria-labelledby="form-dialog-title"
          fullWidth={true}
          cancelAction={cancelAction}
          confirmAction={
            confirmationData.confirmAction === 'confirmActionExportComment'
              ? confirmActionExportComment
              : confirmAction
          }
        >
          <>
            {get(confirmationData, 'shuldShowAnnounecments', false) ? (
              <Announcement label="" type="info" spaced={false} component={'div'}>
                {t('eldaxCheckAnnouncement')}
              </Announcement>
            ) : (
              <></>
            )}
          </>
        </ConfirmationDialog>
      )}
    </>
  );
};

export const StudyDetailComments = React.memo(PlainStudyDetailComments, stateIsSame);
